import React from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface RoleSelectionScreenProps {
  onRoleSelect: (role: 'SuperAdmin' | 'SchoolAdmin' | 'Teacher' | 'Student') => void;
}

const RoleSelectionScreen: React.FC<RoleSelectionScreenProps> = ({ onRoleSelect }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-light/20 via-white to-secondary-light/20 px-4 relative overflow-hidden">
      {/* Decorative shapes */}
      <div className="absolute top-0 left-0 w-40 h-40 bg-primary-light/20 rounded-full filter blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-60 h-60 bg-secondary-light/20 rounded-full filter blur-3xl"></div>
      <div className="absolute top-1/4 right-1/4 w-20 h-20 bg-accent-light/20 rounded-full filter blur-xl"></div>
      
      <div className="w-full max-w-6xl relative z-10">
        <div className="text-center mb-8">
          <img 
            src="/assets/images/swunmath_logo.png" 
            alt="Swun Math Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Welcome to Swun Math
          </h1>
          <p className="text-lg text-gray-600">
            Select your role to continue
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Role Cards */}
          {[
            {
              role: 'SuperAdmin',
              title: 'Administrator',
              description: 'Manage districts, schools, teachers and content',
              buttonText: 'Continue as Super Admin',
              bgGradient: 'from-white to-blue-50',
              iconBg: 'from-blue-100 to-blue-200',
              iconColor: 'text-primary',
              icon: (
                <path 
                  fillRule="evenodd" 
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" 
                  clipRule="evenodd"
                />
              )
            },
            {
              role: 'SchoolAdmin',
              title: 'School Admin',
              description: 'Manage school resources, teachers and student accounts',
              buttonText: 'Continue as Admin',
              bgGradient: 'from-white to-purple-50',
              iconBg: 'from-purple-100 to-purple-200',
              iconColor: 'text-purple-600',
              icon: (
                <path 
                  fillRule="evenodd" 
                  d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h6v4H7V5zm8 8v2h1v1H4v-1h1v-2a7.001 7.001 0 0113 0z" 
                  clipRule="evenodd"
                />
              )
            },
            {
              role: 'Teacher',
              title: 'Teacher',
              description: 'Create learning paths, manage classes and track student progress',
              buttonText: 'Continue as Teacher',
              bgGradient: 'from-white to-green-50',
              iconBg: 'from-green-100 to-green-200',
              iconColor: 'text-secondary',
              icon: (
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              )
            },
            {
              role: 'Student',
              title: 'Student',
              description: 'Follow learning paths, play educational games',
              buttonText: 'Continue as Student',
              bgGradient: 'from-white to-yellow-50',
              iconBg: 'from-yellow-100 to-yellow-200',
              iconColor: 'text-accent',
              icon: (
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              )
            }
          ].map((roleCard, index) => (
            <Card 
              key={index}
              className={`p-6 text-center transition-all duration-200 hover:shadow-lg bg-gradient-to-b ${roleCard.bgGradient} h-full`}
              hoverable
              onClick={() => {
                console.log('Selected role in RoleSelectionScreen:', roleCard.role);
                onRoleSelect(roleCard.role as any);
              }}
            >
              <div className="flex flex-col h-full">
                <div className={`w-20 h-20 bg-gradient-to-br ${roleCard.iconBg} rounded-full flex items-center justify-center mx-auto mb-4 shadow-md`}>
                  <svg 
                    className={`w-10 h-10 ${roleCard.iconColor}`} 
                    fill="currentColor" 
                    viewBox="0 0 20 20" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {roleCard.icon}
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-2">{roleCard.title}</h2>
                <p className="text-gray-600 mb-4 flex-grow">
                  {roleCard.description}
                </p>
                <div className="mt-auto">
                  <Button 
                    variant="primary" 
                    fullWidth 
                    className="min-h-[44px] whitespace-nowrap"
                  >
                    {roleCard.buttonText}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="mt-12 text-center text-gray-500 text-sm relative z-10">
          <p>&copy; {new Date().getFullYear()} Swun Math. All rights reserved.</p>
          <p>Version 1.0.0</p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionScreen; 